import {
  ForwardedRef,
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useBlocker } from "react-router-dom";
import NavigationStepperCst from "@components/NavigationStepperCst";
import { WizardCstProps, WizardHandle } from "./types";
import { useWizardState } from "./hooks/useWizardState";
import { WizardContainer, WizardStepperRow, WizardContent } from "./style";
import WizardHeader from "./components/WizardHeader";
import WizardFooter from "./components/WizardFooter";
import WizardResetDialog from "./components/WizardResetDialog";
import WizardAbandonDialog from "./components/WizardAbandonDialog";

function WizardCstInner<TData>(
  {
    title,
    breadcrumb,
    backLink,
    headerAction,
    steps,
    initialData,
    onComplete,
    resetConfirm,
    abandonMode,
    nextLabel = "Prosegui",
    resetLabel = "Reset",
    completeLabel = "Crea",
    backLabel = "Indietro",
    secondaryAction,
    isCompleting,
  }: WizardCstProps<TData>,
  ref: ForwardedRef<WizardHandle<TData>>,
) {
  const {
    activeStep,
    data,
    setData,
    errors,
    tryNext,
    goBack,
    goToStep,
    reset,
    isFirstStep,
    isLastStep,
    isDirty,
  } = useWizardState({ steps, initialData });

  const [isPersisted, setIsPersisted] = useState(false);
  const isPersistedRef = useRef(false);

  const shouldBlock = !!abandonMode && isDirty && !isPersisted;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !isPersistedRef.current &&
      shouldBlock &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  const [abandonDialogOpen, setAbandonDialogOpen] = useState(false);

  useEffect(() => {
    if (blocker.state === "blocked") setAbandonDialogOpen(true);
  }, [blocker.state]);

  useImperativeHandle(ref, () => ({ setData }), [setData]);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const handleNext = async () => {
    if (isCompleting) return;
    if (isLastStep) {
      const last = steps[activeStep];
      if (last?.validate) {
        const result = last.validate(data);
        if (!result.valid) {
          tryNext();
          return;
        }
      }
      try {
        await onComplete(data);
        isPersistedRef.current = true;
        setIsPersisted(true);
      } catch {
        /* consumer gestisce l'errore; il blocker resta attivo */
      }
      return;
    }
    tryNext();
  };

  const handleResetClick = () => {
    if (!resetConfirm) {
      reset();
      return;
    }
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    setResetDialogOpen(false);
    reset();
  };

  const ActiveStepComponent = steps[activeStep]?.Component;
  const stepperSteps = steps.map((s) => ({ key: s.key, label: s.label }));

  return (
    <WizardContainer>
      <WizardHeader
        title={title}
        breadcrumb={breadcrumb}
        backLink={backLink}
        headerAction={headerAction}
      />

      <WizardStepperRow>
        <NavigationStepperCst
          steps={stepperSteps}
          activeStep={activeStep}
          onStepChange={goToStep}
          allowBackNavigation
        />
      </WizardStepperRow>

      <WizardContent>
        {ActiveStepComponent && (
          <ActiveStepComponent data={data} setData={setData} errors={errors} />
        )}
      </WizardContent>

      <WizardFooter
        resetLabel={resetLabel}
        nextLabel={isLastStep ? completeLabel : nextLabel}
        backLabel={backLabel}
        onReset={handleResetClick}
        onNext={handleNext}
        onBack={goBack}
        showBack={!isFirstStep}
        secondaryAction={
          isLastStep && secondaryAction
            ? {
                label: secondaryAction.label,
                onClick: () => secondaryAction.onClick(data),
                disabled: secondaryAction.disabled,
              }
            : undefined
        }
        isCompleting={isCompleting}
      />

      {resetConfirm && (
        <WizardResetDialog
          open={resetDialogOpen}
          onClose={() => setResetDialogOpen(false)}
          onConfirm={handleResetConfirm}
          copy={resetConfirm}
        />
      )}

      {abandonMode && (
        <WizardAbandonDialog
          open={abandonDialogOpen}
          mode={abandonMode}
          onClose={() => {
            setAbandonDialogOpen(false);
            if (blocker.state === "blocked") blocker.reset();
          }}
          onAbandon={() => {
            isPersistedRef.current = true;
            setAbandonDialogOpen(false);
            if (blocker.state === "blocked") blocker.proceed();
          }}
          onSaveDraft={
            secondaryAction
              ? () => {
                  isPersistedRef.current = true;
                  setAbandonDialogOpen(false);
                  if (blocker.state === "blocked") blocker.reset();
                  void secondaryAction.onClick(data);
                }
              : undefined
          }
        />
      )}
    </WizardContainer>
  );
}

const WizardCst = forwardRef(WizardCstInner) as <TData>(
  props: WizardCstProps<TData> & { ref?: Ref<WizardHandle<TData>> },
) => ReturnType<typeof WizardCstInner>;

export default WizardCst;
